import { useEffect, useMemo, useState } from 'react';
import { fieldReader, fieldWriter, fetchLoanConditions } from '../../services/loanApi';
import { getAccessToken } from '../../services/authApi';
import { getZipLocations } from '../../services/locationServiceApi';
import US_STATES from '../../data/usStates';
import Toast from '../shared/Toast';
import LoadingOverlay from '../shared/LoadingOverlay';
import { menuItemsCP, menuItemsBP } from '../menu/menuItems';
import { collectConditionFields, evaluateCompositeCondition } from '../../utils/conditionUtils';
import { checkValidationRequired } from '../../utils/validationRedirect';
import { runLicensingGateForPage } from '../../utils/licenseValidation';
import { getLockStatusNote, isPollyProcessingStatus } from '../../utils/pricingValidation';
import elliemaeApi from '../../services/interceptors/elliemaeApi';
import { getBlockedLoanProgramName } from '../../utils/loanProgramUtil';
import conditionDefinitions from '../menu/conditions.json';
import { isLoanProgramDisabledForOrg, getLoanProgramLabel, toCanonicalLoanProgram } from '../../utils/loanProgramUtil';

const API_DOMAIN = process.env.REACT_APP_TPOC_API_DOMAIN;
const isConditionBetaEnabled = process.env.REACT_APP_ENABLE_CONDITIONBETA_PAGE === 'true';

const resolveAiDocReviewApiBaseUrl = () => {
    if (API_DOMAIN) {
        return `${API_DOMAIN}/api/recommended-doc-review`;
    }

    return '/api/recommended-doc-review';
};

const SubmitLoanPageRenderer = ({ pageName, config, redirectPage }) => {
    const isGuidelineExceptionPage = pageName === 'Request Guideline Exception';

    // auth + loan
    const [token, setToken] = useState(null);
    const [loanId, setLoanId] = useState('');

    // data
    const [fieldValues, setFieldValues] = useState({});
    const [pageConfig, setPageConfig] = useState(null);

    // ui
    const [loading, setLoading] = useState(true);
    const [loadingText, setLoadingText] = useState('Loading...');

    // toast (animated like your original)
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastVisible, setToastVisible] = useState(false);
    const [toastAnimation, setToastAnimation] = useState('animate-fade-in-right');

    // zip popup
    const [showZipPopup, setShowZipPopup] = useState(false);
    const [cityOptions, setCityOptions] = useState([]);
    const [zipSelection, setZipSelection] = useState({ cityId: '', stateId: '' });

    // lock comparison state (only when 4788 === "Needs Validation")
    const [lcFields, setLcFields] = useState([]);
    const [lcStatus, setLcStatus] = useState('');
    const [lcLockDate, setLcLockDate] = useState('');
    const [lcExpire, setLcExpire] = useState('');
    const [licenseStatus, setLicenseStatus] = useState({
        blocked: false,
        companyRequired: false,
        loRequired: false,
        companyHas: false,
        loHas: false,
        stateCode: '',
        stateName: '',
        reason: ''
    });
    const [brokerSelectionMode, setBrokerSelectionMode] = useState('');
    const [skipModuleValidations, setSkipModuleValidations] = useState(false);
    const [aiDocReviewPendingCount, setAiDocReviewPendingCount] = useState(null);
    const [aiDocReviewStatusLoaded, setAiDocReviewStatusLoaded] = useState(false);
    const [uploadConditionsNeedsValidation, setUploadConditionsNeedsValidation] = useState(false);
    const [uploadConditionsStatusLoaded, setUploadConditionsStatusLoaded] = useState(false);
    const [uploadConditionsValidationItems, setUploadConditionsValidationItems] = useState([]);

    // helpers
    const isEmptyField = (val) => {
        const trimmed = (val || '').toString().trim();
        return trimmed === '' || trimmed === '//' || trimmed === '  /  /    ';
    };


    const getBorrowerPairCount = (values) => {
        const raw = values["4460"];
        const count = parseInt(raw, 10);
        return Number.isFinite(count) && count > 0 ? count : 1;
    };

    const getMissingOrderCreditPairFields = (values) => {
        const pairCount = getBorrowerPairCount(values);
        const missing = [];

        for (let pairIndex = 1; pairIndex <= pairCount; pairIndex++) {
            const fieldId = `300#${pairIndex}`;
            if (isEmptyField(values[fieldId])) {
                missing.push(fieldId);
            }
        }

        return missing;
    };

    const isOrderCreditCleared = (values) => {
        return getMissingOrderCreditPairFields(values).length === 0;
    };

    const getModuleCompletionFieldIds = (group) => {
        const completionFields = (group.completionFields || []).filter(Boolean);
        if (completionFields.length > 0) return completionFields;

        return (group.Fields || [])
            .filter(field => field?.id)
            .map(field => field.id);
    };


    const normalizeDate = (date) =>
        new Date(date.getFullYear(), date.getMonth(), date.getDate());

    const showToastMsg = (message) => {
        setToastMessage(message);
        setShowToast(true);
    };

    const fetchAiDocReviewPendingCount = async (loanNumber) => {
        if (!loanNumber) {
            setAiDocReviewPendingCount(null);
            setAiDocReviewStatusLoaded(true);
            return;
        }

        setAiDocReviewStatusLoaded(false);

        try {
            const apiUrl = `${resolveAiDocReviewApiBaseUrl()}/loan/${encodeURIComponent(loanNumber)}`;
            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            });

            let payload = null;
            try {
                payload = await response.json();
            } catch {
                payload = null;
            }

            if (!response.ok) {
                throw new Error(payload?.message || 'Failed to load Document Review status.');
            }

            const collection = Array.isArray(payload?.data)
                ? payload.data
                : Array.isArray(payload)
                    ? payload
                    : Array.isArray(payload?.documents)
                        ? payload.documents
                        : Array.isArray(payload?.items)
                            ? payload.items
                            : [];

            const pendingCount = collection.filter((item) => {
                const rawStatus = item?.userAcceptStatus ?? item?.UserAcceptStatus ?? 'Pending';
                return String(rawStatus).trim().toLowerCase() === 'pending';
            }).length;

            setAiDocReviewPendingCount(pendingCount);
        } catch (error) {
            console.error('Failed to fetch AI Document Review pending count:', error);
            setAiDocReviewPendingCount(null);
        } finally {
            setAiDocReviewStatusLoaded(true);
        }
    };

    const getAssignedDocumentEntityIds = (condition) => {
        const assigned = Array.isArray(condition?.assignedTo) ? condition.assignedTo : [];

        return assigned
            .filter((entry) => entry && typeof entry === 'object')
            .filter((entry) => (entry.entityType || '').toString().trim().toLowerCase() === 'document')
            .map((entry) => (entry.entityId || '').toString().trim())
            .filter(Boolean);
    };

    const fetchUploadConditionsValidation = async (targetLoanId) => {
        setUploadConditionsStatusLoaded(false);
        try {
            const authObject = await window.elli.script.getObject('auth');
            const authCode = await authObject.createAuthCode();
            const accessToken = await getAccessToken(authCode);
            const allConditions = await fetchLoanConditions(accessToken, targetLoanId);
            const { data: documentsPayload } = await elliemaeApi.get(
                `/encompass/v3/loans/${targetLoanId}/documents?includeRemoved=true&requireActiveAttachments=false`,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        Accept: 'application/json'
                    }
                }
            );
            const loanDocuments = Array.isArray(documentsPayload) ? documentsPayload : [];

            const documentHasAttachmentsById = new Map(
                loanDocuments
                    .map((doc) => {
                        const documentId = (doc?.id || '').toString().trim();
                        const hasAttachments = Array.isArray(doc?.attachments) && doc.attachments.length > 0;
                        return [documentId, hasAttachments];
                    })
                    .filter(([documentId]) => Boolean(documentId))
            );

            const requiredPreInternalIds = new Set(
                (Array.isArray(conditionDefinitions) ? conditionDefinitions : [])
                    .filter((conditionDef) =>
                        conditionDef?.required === true &&
                        (conditionDef?.conditionType || '').toString().trim().toLowerCase() === 'pre'
                    )
                    .map((conditionDef) => (conditionDef?.internalId || '').toString().trim().toUpperCase())
                    .filter(Boolean)
            );

            const requiredPreConditionByInternalId = new Map(
                (Array.isArray(conditionDefinitions) ? conditionDefinitions : [])
                    .filter((conditionDef) =>
                        conditionDef?.required === true &&
                        (conditionDef?.conditionType || '').toString().trim().toLowerCase() === 'pre'
                    )
                    .map((conditionDef) => [
                        (conditionDef?.internalId || '').toString().trim().toUpperCase(),
                        conditionDef
                    ])
                    .filter(([internalId]) => Boolean(internalId))
            );

            const matchedRequiredPreConditions = (Array.isArray(allConditions) ? allConditions : []).filter((condition) => {
                const conditionInternalId =
                    (condition?.internalId || condition?.conditionId || '')
                        .toString()
                        .trim()
                        .toUpperCase();

                return requiredPreInternalIds.has(conditionInternalId);
            });

            const failedConditions = matchedRequiredPreConditions
                .map((condition) => {
                    const conditionInternalId =
                        (condition?.internalId || condition?.conditionId || '')
                            .toString()
                            .trim()
                            .toUpperCase();
                    const definition = requiredPreConditionByInternalId.get(conditionInternalId);

                    const assignedDocumentEntityIds = getAssignedDocumentEntityIds(condition);

                    if (assignedDocumentEntityIds.length === 0) {
                        return {
                            name:
                                (condition?.name || condition?.title || condition?.conditionName || definition?.name || 'Unknown Condition')
                                    .toString(),
                            externalId:
                                (condition?.externalId || condition?.conditionExternalId || definition?.externalId || '')
                                    .toString()
                        };
                    }

                    const hasMissingAttachment = assignedDocumentEntityIds.some((entityId) =>
                        documentHasAttachmentsById.get(entityId) !== true
                    );

                    if (!hasMissingAttachment) {
                        return null;
                    }

                    return {
                        name:
                            (condition?.name || condition?.title || condition?.conditionName || definition?.name || 'Unknown Condition')
                                .toString(),
                        externalId:
                            (condition?.externalId || condition?.conditionExternalId || definition?.externalId || '')
                                .toString()
                    };
                })
                .filter(Boolean);

            const needsValidation = failedConditions.length > 0;

            setUploadConditionsNeedsValidation(needsValidation);
            setUploadConditionsValidationItems(failedConditions);
        } catch (error) {
            console.error('Failed to load upload condition validation status:', error);
            setUploadConditionsNeedsValidation(false);
            setUploadConditionsValidationItems([]);
        } finally {
            setUploadConditionsStatusLoaded(true);
        }
    };

    useEffect(() => {
        let hideTimer, vanishTimer;
        if (showToast) {
            setToastVisible(true);
            setToastAnimation('animate-fade-in-right');
            hideTimer = setTimeout(() => {
                setToastAnimation('animate-fade-out-right');
                vanishTimer = setTimeout(() => {
                    setShowToast(false);
                    setToastVisible(false);
                }, 500);
            }, 4000);
        }
        return () => {
            clearTimeout(hideTimer);
            clearTimeout(vanishTimer);
        };
    }, [showToast]);

    // init
    useEffect(() => {
        const init = async () => {
            try {
                window.elli?.script?.guest?.create?.();

                const loanObject = await window.elli.script.getObject('loan');
                const rawLoan = await loanObject.all();
                const loan = rawLoan?.loan ?? rawLoan;
                setLoanId(loan.id);

                const authObject = await window.elli.script.getObject('auth');
                const authCode = await authObject.createAuthCode();
                const accessToken = await getAccessToken(authCode);
                setToken(accessToken);

                // collect base field ids from JSON Fields
                const baseFieldIds = config.FieldGroups.flatMap((group) =>
                    (group.Fields?.filter((f) => f.id).map((f) => f.id)) || []
                );
                const completionFieldIds = config.FieldGroups.flatMap((group) =>
                    (group.completionFields || []).filter(Boolean)
                );
                // ALSO collect ShowIf field ids so we can evaluate visibility
                const showIfFieldIds = config.FieldGroups.flatMap(group => collectConditionFields(group.ShowIf));

                // build ids to read (loan type + 4788 + all base fields)
                const fieldIds = [
                    ...new Set([
                        'CX.LOAN.TYPE',
                        '4788', // Lock Validation status (also used as button id)
                        '1401',
                        '3238',
                        'CX.COND.INDEXRESPONSIBILITY',
                        'LOCKRATE.CURRENTSTATUS',
                        'CX.TPOC.OVERRIDE.VALIDATIONS',
                        '230',
                        'CX.LEGAL.ENTITY.TYPE',
                        '231',
                        'CX.ALL.DISCLOSURES.SENT',
                        "4460",
                        "300#2",
                        "300#3",
                        "300#4",
                        "300#5",
                        ...baseFieldIds,
                        ...completionFieldIds,
                        ...showIfFieldIds,
                        ...config.FieldGroups.flatMap(group => collectConditionFields(group.NotRequiredIf))
                    ])
                ];

                const values = await fieldReader(accessToken, fieldIds, loan.id);

                await fetchAiDocReviewPendingCount(loan?.loanNumber || '');
                await fetchUploadConditionsValidation(loan.id);

                const overrideValidations =
                    (values['CX.TPOC.OVERRIDE.VALIDATIONS'] || '')
                        .toString()
                        .trim()
                        .toUpperCase();

                const skipModules = overrideValidations === 'Y';
                setSkipModuleValidations(skipModules);

                const loanType = values['CX.LOAN.TYPE'];
                const stateCode = (values['14'] || '').trim();
                const tpoOrgId = (values['TPO.X15'] || '').trim();
                const tpoUserId = (values['TPO.X62'] || '').trim();
                const city = (values['12'] || '').trim();
                const legalEntityType = (values['CX.LEGAL.ENTITY.TYPE'] || '').trim();

                let filteredGroups = config.FieldGroups.filter(
                    (g) => !g.LoanType || g.LoanType === loanType
                );

                if (skipModules) {
                    filteredGroups = filteredGroups.filter(
                        (g) => (g.Type || '').toLowerCase() !== 'module'
                    );
                }

                // Run licensing gate and store result
                let status = {
                    blocked: false,
                    companyRequired: false,
                    loRequired: false,
                    companyHas: false,
                    loHas: false,
                    stateCode: '',
                    stateName: '',
                    reason: '',
                    message: '',
                    brokerSelection: ''
                };

                if (!skipModules && !isGuidelineExceptionPage) {
                    status = await runLicensingGateForPage(
                        loanType,
                        stateCode,
                        tpoOrgId,
                        tpoUserId,
                        city,
                        legalEntityType
                    );
                }

                setLicenseStatus({ ...status, city });
                setBrokerSelectionMode(status.brokerSelection || '');

                // If Business loan, hide "Commerical Loan App" group when the document already exists
                // if ((loanType || '').trim() === 'Business') {
                //     // Find the Business group (by name) and read its 'document' field from JSON
                //     const businessGroup = filteredGroups.find(g => g.Group === 'Commerical Loan App');
                //     const businessDocField = businessGroup?.Fields?.find(f => f.document);
                //     const docTitle = businessDocField?.document;

                //     if (docTitle) {
                //         const docId = await checkDocumentByAssignedEntity(accessToken, loan.id, docTitle);
                //         const businessDocMissing = !docId;

                //         // If document exists, remove the "Commerical Loan App" group from the page
                //         if (!businessDocMissing) {
                //             filteredGroups = filteredGroups.filter(g => g.Group !== 'Commerical Loan App');
                //         }
                //     }
                // }

                //hide "Send Disclosure" group when the disclosure already sent

                if (!skipModules) {
                    if (status.brokerSelection !== 'GenerateLEAndDisclosures') {
                        filteredGroups = filteredGroups.filter(g => g.Group !== 'Send Disclosures');
                    }
                    else {
                        const allDisclosuresSent =
                            (values['CX.ALL.DISCLOSURES.SENT'] || '').toString().trim().toUpperCase() === 'Y';

                        if (allDisclosuresSent) {
                            filteredGroups = filteredGroups.filter(g => g.Group !== 'Send Disclosures');
                        }
                    }
                }


                if (!skipModules && status.blocked) {
                    filteredGroups = [
                        ...filteredGroups.filter(g => g.Group === 'Licensing'),
                        ...filteredGroups.filter(g => g.Group !== 'Licensing'),
                    ];
                }

                setFieldValues(values);
                setPageConfig({ ...config, FieldGroups: filteredGroups });
            } catch (error) {
                console.error(`Error initializing ${pageName} page:`, error);
            } finally {
                setLoading(false);
            }
        };
        init();
    }, []);

    // fetch lockComparison only when 4788 === "Needs Validation"
    useEffect(() => {
        if (isGuidelineExceptionPage) return;

        const needs = (fieldValues['4788'] || '').trim() === 'Needs Validation';
        if (!needs || !loanId || !token) return;

        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                const { data } = await elliemaeApi.get(
                    `/secondary/v1/loans/${loanId}/lockComparison`,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json'
                        }
                    }
                );
                if (cancelled) return;
                setLcFields(data.fields || []);
                setLcStatus(data.lockValidationStatus || '');
                setLcLockDate(data.lockDate || '');
                setLcExpire(data.lockExpirationDate || '');
            } catch (e) {
                console.error('Lock comparison fetch failed:', e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [fieldValues['4788'], loanId, token]);

    // visibility rules
    const shouldShowGroup = (group) => {
        if (skipModuleValidations && group.Type === 'Module') {
            return false;
        }

        // Show Licensing group only when the gate fails
        if (group.Group === 'Licensing') {
            return !skipModuleValidations && licenseStatus.blocked;
        }

        if (group.Group === 'Order Credit') {
            if (isGroupNotRequired(group, fieldValues)) return false;
            return !isOrderCreditCleared(fieldValues);
        }

        if (group.Group === 'Upload Documents') {
            if (!isConditionBetaEnabled) {
                return false;
            }

            // Do not render until loan condition validation status is loaded.
            if (!uploadConditionsStatusLoaded) {
                return false;
            }

            // Show only when at least one required/pre condition has empty attachement.
            return uploadConditionsNeedsValidation;
        }

        if (group.Type === 'Module' && (group.completionFields || []).length > 0) {
            return !isModuleGroupCleared(group);
        }

        // "Request Disclosure" is always shown (so it will be validated alongside others).
        if (brokerSelectionMode === 'RequestLEAndDisclosures') {
            if (group.Group === 'Request Disclosure') return true;
            // otherwise, fall through to normal behavior so other groups still show as appropriate
        } else {
            // When NOT in RequestLEAndDisclosures mode, hide the Request Disclosure group entirely.
            if (group.Group === 'Request Disclosure') return false;
        }

        // Special: Pricing should show when:
        // - Order Credit is completed (300 not empty) OR
        // - Order Credit is NOT required (its NotRequiredIf condition is satisfied)
        if (group.Group === 'Pricing') {
            const creditVal = (fieldValues['300'] ?? '').toString().trim();

            // Find the Order Credit group from the same config and evaluate NotRequiredIf
            const orderCreditGroup =
                (pageConfig?.FieldGroups || config?.FieldGroups || []).find(g => g.Group === 'Order Credit');

            const orderCreditNotRequired =
                orderCreditGroup?.NotRequiredIf
                    ? evaluateCompositeCondition(orderCreditGroup.NotRequiredIf, fieldValues)
                    : false;

            return creditVal !== '' || orderCreditNotRequired;
        }

        // Loan program blocked for this org (ChampsTPO: ACTIVATOR PRIME) => block Submit Loan
        if (group.Group === 'Loan Program Not Available') {
            return !skipModuleValidations &&
                Boolean(getBlockedLoanProgramName(fieldValues[group.ShowIf?.field], fieldValues['1401']));
        }

        // Special: Lock Comparison only when 4788 === "Needs Validation"
        if (group.Group === 'Lock Comparison') {
            return !skipModuleValidations &&
                (fieldValues['4788'] ?? '').toString().trim() === 'Needs Validation';
        }

        if (group.Group === 'Rate Lock — Processing') {
            return !skipModuleValidations &&
                isPollyProcessingStatus(
                    fieldValues['LOCKRATE.CURRENTSTATUS'],
                    fieldValues['1401'],
                    fieldValues['CX.LOAN.TYPE']
                );
        }

        // Special: Mortgage Fees should show ONLY when Borrower Paid AND 389 is empty
        if (group.Group === 'Mortgage Fees') {
            const isBorrowerPaid =
                (fieldValues['LCP.X1'] || '').trim().toLowerCase() === 'borrower paid';
            if (!isBorrowerPaid) return false;

            // If 389 already has a value, don't show the group
            const feeEmpty = isEmptyField(fieldValues['389']);
            return feeEmpty;
        }


        if (group.Group === 'Insurance and Fees') {
            const homeownerAmountFilled = !isEmptyField(fieldValues['230']);
            const homeownerMonthsFilled = !isEmptyField(fieldValues['1387']);
            const mortgageAmountFilled = !isEmptyField(fieldValues['231']);
            const mortgageMonthsFilled = !isEmptyField(fieldValues['1386']);

            const homeownerSatisfied = !homeownerAmountFilled || (homeownerAmountFilled && homeownerMonthsFilled);
            const mortgageSatisfied = !mortgageAmountFilled || (mortgageAmountFilled && mortgageMonthsFilled);

            if (homeownerSatisfied && mortgageSatisfied) {
                return false;
            }
            //hide the group
            return true;
        }

        // Generic ShowIf handling
        const condition = group.ShowIf;
        if (condition) {
            const raw = fieldValues[condition.field];
            const val = (raw ?? '').toString().trim();

            // generic string equals (case-insensitive)
            if (condition.operator === 'equals') {
                const target = (condition.value ?? '').toString().trim();
                return val.localeCompare(target, undefined, { sensitivity: 'accent' }) === 0;
            }

            // simple empty/notEmpty
            if (condition.operator === 'notEmpty') return val !== '';
            if (condition.operator === 'empty') return val === '';

            // date vs today
            const fieldDateObj = normalizeDate(new Date(val));
            const todayObj = normalizeDate(new Date());
            if (isNaN(fieldDateObj)) return true;

            switch (condition.operator) {
                case 'lt':
                    return fieldDateObj < todayObj;
                case 'lte':
                    return fieldDateObj <= todayObj;
                case 'eq':
                    return fieldDateObj.getTime() === todayObj.getTime();
                case 'gte':
                    return fieldDateObj >= todayObj;
                case 'gt':
                    return fieldDateObj > todayObj;
                default:
                    return false;
            }
        }

        // For Field groups without ShowIf:
        // show if any visible field is empty OR fails validation (e.g., notTBD)
        if (group.Type === 'Field') {
            const needsAttention = group.Fields?.some((f) => {
                if (f.hidden) return false;
                const v = fieldValues[f.id];
                return isEmptyField(v) || isInvalidByValidate(f);
            });
            return !!needsAttention;
        }

        // Modules without ShowIf default to show
        return true;
    };

    const isGroupNotRequired = (group) =>
        group?.NotRequiredIf ? evaluateCompositeCondition(group.NotRequiredIf, fieldValues) : false;

    const isModuleGroupCleared = (group) => {
        if (skipModuleValidations && group.Type === 'Module') {
            return true;
        }

        if (!skipModuleValidations && group.Group === 'Licensing' && licenseStatus.blocked) {
            return false;
        }

        // Order Credit: cleared only when all borrower pair credit fields are filled
        if (group.Group === "Order Credit") {
            if (isGroupNotRequired(group, fieldValues)) return true;
            return isOrderCreditCleared(fieldValues);
        }

        // Lock Comparison is never considered "cleared" by field fill
        if (group.Group === 'Lock Comparison') return false;

        if (group.Group === 'Rate Lock — Processing') return false;

        if (isGroupNotRequired(group)) return true;

        const completionFieldIds = getModuleCompletionFieldIds(group);

        if (!completionFieldIds.length) return false;
        return completionFieldIds.every((fieldId) => {
            const value = fieldValues[fieldId];
            return !isEmptyField(value);
        });
    };

    const allModulesCleared = useMemo(() => {
        if (!pageConfig) return false;

        if (skipModuleValidations) return true;

        return pageConfig.FieldGroups
            .filter((group) => group.Type === 'Module')
            .filter((group) => shouldShowGroup(group))
            .every((group) => isModuleGroupCleared(group));
    }, [
        pageConfig,
        fieldValues,
        aiDocReviewPendingCount,
        aiDocReviewStatusLoaded,
        uploadConditionsNeedsValidation,
        uploadConditionsStatusLoaded
    ]);

    const handleZipBlur = async (field) => {
        if (field.datatype !== 'Zip') return;
        const zip = document.getElementById(`field-${field.id}`)?.value;
        if (!zip || zip.length < 5) return;

        const results = await getZipLocations(zip, token);
        const { city: cityId, state: stateId } = field;

        if (results.length === 1) {
            document.getElementById(`field-${cityId}`).value = results[0].city;
            document.getElementById(`field-${stateId}`).value = results[0].abbrev;
        } else if (results.length > 1) {
            setCityOptions(results);
            setZipSelection({ cityId, stateId });
            setShowZipPopup(true);
        }
    };

    // returns true if a field fails its JSON "validate" rule
    const isInvalidByValidate = (field) => {
        if (!field?.validate) return false;
        const current = (fieldValues[field.id] ?? '').toString().trim();
        if (field.validate === 'notTBD') {
            return current.toUpperCase() === 'TBD';
        }
        return false;
    };
    // allow only digits, one leading -, and one dot; clamp to range; fix precision
    const normalizeDecimal = (raw, { min = 0, max = 100, decimals = 3 } = {}) => {
        let s = String(raw ?? '').trim();

        // strip everything except digits, dot, minus
        s = s.replace(/[^\d.-]/g, '');

        // only one minus at start
        s = s.replace(/(?!^)-/g, '');

        // only one dot
        s = s.replace(/(\..*)\./g, '$1');

        if (s === '' || s === '-' || s === '.') return '';

        let n = Number(s);
        if (Number.isNaN(n)) return '';

        // clamp to [min, max]
        if (n < min) n = min;
        if (n > max) n = max;

        // fix precision safely
        const pow = Math.pow(10, decimals);
        n = Math.round(n * pow) / pow;

        // keep trailing zeros consistent with decimals user typed; default to up to `decimals`
        return n.toString();
    };

    // live keystroke guard for decimal fields
    const filterDecimalKeystrokes = (value) =>
        value
            .replace(/[^\d.-]/g, '')       // only digits, dot, minus
            .replace(/(?!^)-/g, '')        // minus only at start
            .replace(/(\..*)\./g, '$1');   // only one dot


    const isFieldCurrentlyRequired = (field, uiValue, allFieldValues) => {
        if (field.requiredIf && field.requiredIf.field) {
            const triggerId = field.requiredIf.field;
            const triggerOp = field.requiredIf.operator;
            const triggerVal = (document.getElementById(`field-${triggerId}`)?.value ?? allFieldValues[triggerId] ?? '').toString().trim();

            if (triggerOp === 'notEmpty') {
                return triggerVal !== '';
            }
            if (triggerOp === 'empty') {
                return triggerVal === '';
            }
        }

        return true;
    };

    const handleSave = async () => {
        const visibleFieldGroups = pageConfig?.FieldGroups
            .filter((group) => group.Type === 'Field')
            .filter((group) => shouldShowGroup(group));

        const updated = [];
        const missingFields = [];
        let atLeastOneFilled = false;

        visibleFieldGroups?.forEach((group) => {
            group.Fields.forEach((field) => {
                if (field.hidden) return;

                const el = document.getElementById(`field-${field.id}`);
                let uiValue = el?.value?.trim();
                const storedValue = (fieldValues[field.id] ?? '').toString().trim();

                if (field.datatype === 'decimal') {
                    // Allow per-field overrides from JSON, fallback to 0..100 and 3 decimals
                    const min = typeof field.min === 'number' ? field.min : 0;
                    const max = typeof field.max === 'number' ? field.max : 100;
                    const decimals = typeof field.decimals === 'number' ? field.decimals : 3;

                    uiValue = normalizeDecimal(uiValue, { min, max, decimals });

                }
                const effectiveValue = uiValue !== undefined ? uiValue : storedValue;

                const requiredNow = isFieldCurrentlyRequired(field, uiValue, fieldValues);

                // If field is required now and empty -> missing
                if (requiredNow && (!effectiveValue || effectiveValue === '')) {
                    missingFields.push(field.name);
                } else if (!requiredNow && (!effectiveValue || effectiveValue === '')) {
                    // not required AND empty -> don't flag as missing
                } else {
                    // field has a value; consider for update
                    atLeastOneFilled = true;
                    if (uiValue !== undefined && uiValue !== storedValue) {
                        updated.push({ id: field.id, value: uiValue });
                    }
                }

                // Application Date special check
                if (field.id === '745') {
                    const normalizedUI = (uiValue ?? '').trim();
                    const today = new Date().toISOString().split('T')[0];
                    if (!normalizedUI) {
                        missingFields.push('Application Date is required.');
                    } else if (normalizedUI > today) {
                        missingFields.push('Application Date cannot be a future date.');
                    }
                }

                // Dynamic "notTBD" validator from JSON
                if (field.validate === 'notTBD') {
                    const v = (uiValue ?? storedValue ?? '').trim().toUpperCase();
                    if (v === 'TBD') {
                        missingFields.push(`${field.name} cannot be TBD.`);
                    }
                }
            });
        });

        if (!atLeastOneFilled) {
            showToastMsg('Please fill at least one required field.');
            return;
        }

        if (missingFields.length > 0) {
            showToastMsg(
                `The following required fields are missing or need update:<br>- ${missingFields.join(
                    '<br>- '
                )}`
            );
            return;
        }

        const onlyAppDateMessages = missingFields.length > 0 &&
            missingFields.every(msg =>
                msg === "Application Date is required." ||
                msg === "Application Date cannot be a future date."
            );

        if (onlyAppDateMessages) {
            return;
        }

        setLoadingText('Saving...');
        setLoading(true);
        try {
            if (updated.length > 0) {
                await fieldWriter(token, loanId, updated);
            }
            // collect base field ids from JSON Fields
            const baseFieldIds = config.FieldGroups.flatMap((group) =>
                (group.Fields?.filter((f) => f.id).map((f) => f.id)) || []
            );
            // ALSO collect ShowIf field ids so we can evaluate visibility
            const showIfFieldIds = config.FieldGroups
                .map(g => g.ShowIf?.field)
                .filter(Boolean);

            // build ids to read (loan type + 4788 + all base fields)
            const fieldIds = [
                ...new Set([
                    'CX.LOAN.TYPE',
                    '4788', // Lock Validation status (also used as button id)
                    '1401',
                    '3238',
                    'CX.COND.INDEXRESPONSIBILITY',
                    'LOCKRATE.CURRENTSTATUS',
                    'CX.TPOC.OVERRIDE.VALIDATIONS',
                    '230',
                    '231',
                    'CX.LEGAL.ENTITY.TYPE',
                    'CX.ALL.DISCLOSURES.SENT',
                    "4460",
                    "300#2",
                    "300#3",
                    "300#4",
                    "300#5",
                    ...baseFieldIds,
                    ...showIfFieldIds,
                    ...config.FieldGroups.flatMap(group => collectConditionFields(group.NotRequiredIf))
                ])
            ];

            const values = await fieldReader(token, fieldIds, loanId);

            const overrideValidations =
                (values['CX.TPOC.OVERRIDE.VALIDATIONS'] || '')
                    .toString()
                    .trim()
                    .toUpperCase();

            const skipModules = overrideValidations === 'Y';
            setSkipModuleValidations(skipModules);

            const loanType = values['CX.LOAN.TYPE'];
            const stateCode = (values['14'] || '').trim();
            const tpoOrgId = (values['TPO.X15'] || '').trim();
            const tpoUserId = (values['TPO.X62'] || '').trim();
            const city = (values['12'] || '').trim();
            const legalEntityType = (values['CX.LEGAL.ENTITY.TYPE'] || '').trim();
            let filteredGroups = config.FieldGroups.filter(
                (g) => !g.LoanType || g.LoanType === loanType
            );

            if (skipModules) {
                filteredGroups = filteredGroups.filter(
                    (g) => (g.Type || '').toLowerCase() !== 'module'
                );
            }

            // Run licensing gate and store result
            let status = {
                blocked: false,
                companyRequired: false,
                loRequired: false,
                companyHas: false,
                loHas: false,
                stateCode: '',
                stateName: '',
                reason: '',
                message: '',
                brokerSelection: ''
            };

            if (!skipModules) {
                status = await runLicensingGateForPage(
                    loanType,
                    stateCode,
                    tpoOrgId,
                    tpoUserId,
                    city,
                    legalEntityType
                );
            }

            setLicenseStatus({ ...status, city });
            setBrokerSelectionMode(status.brokerSelection || '');

            // NEW: If Business loan, hide "Commerical Loan App" group when the document already exists
            // if ((loanType || '').trim() === 'Business') {
            //     // Find the Business group (by name) and read its 'document' field from JSON
            //     const businessGroup = filteredGroups.find(g => g.Group === 'Commerical Loan App');
            //     const businessDocField = businessGroup?.Fields?.find(f => f.document);
            //     const docTitle = businessDocField?.document;

            //     if (docTitle) {
            //         const docId = await checkDocumentByAssignedEntity(token, loanId, docTitle);
            //         const businessDocMissing = !docId;

            //         // If document exists, remove the "Commerical Loan App" group from the page
            //         if (!businessDocMissing) {
            //             filteredGroups = filteredGroups.filter(g => g.Group !== 'Commerical Loan App');
            //         }
            //     }
            // }

            //hide "Send Disclosure" group when the disclosure already sent

            if (!skipModules) {
                if (status.brokerSelection !== 'GenerateLEAndDisclosures') {
                    filteredGroups = filteredGroups.filter(g => g.Group !== 'Send Disclosures');
                }
                else {
                    const allDisclosuresSent =
                        (values['CX.ALL.DISCLOSURES.SENT'] || '').toString().trim().toUpperCase() === 'Y';

                    if (allDisclosuresSent) {
                        filteredGroups = filteredGroups.filter(g => g.Group !== 'Send Disclosures');
                    }
                }
            }


            if (!skipModules && status.blocked) {
                filteredGroups = [
                    ...filteredGroups.filter(g => g.Group === 'Licensing'),
                    ...filteredGroups.filter(g => g.Group !== 'Licensing'),
                ];
            }

            setFieldValues(values);
            setPageConfig({ ...config, FieldGroups: filteredGroups });

            if (values['CX.DISCLOSURE.GEN.DT']) {
                const disclosuredate = new Date(values['CX.DISCLOSURE.GEN.DT']);
                const today = new Date();
                const cutoffDate = new Date(disclosuredate.toISOString().split('T')[0]);
                const todayDate = new Date(today.toISOString().split('T')[0]);
                if (cutoffDate < todayDate) {
                    showToastMsg(
                        "The Initial Disclosures window is closed based on your application date.<br>Please update the application date if it's incorrect."
                    );
                    return;
                }
            }

            await redirectToPage(redirectPage, loanId);
        } catch (err) {
            console.error("Save failed", err);

            // Show only the error details string
            const message = err?.response?.data?.errors?.[0]?.details
                || err?.response?.data?.details
                || "Save failed. Please try again.";

            showToastMsg(message);
            setLoading(false);
            return;
        } finally {
            setLoading(false);
        }
    };

    const redirectToPage = async (page, loanId) => {
        const tpoAppObject = await window.elli.script.getObject("tpoApplication");

        const loanTypeValue = fieldValues["CX.LOAN.TYPE"]?.toLowerCase() || "";
        const loanOfficerNMLSID = fieldValues["3238"]?.trim(); // NMLS Field ID

        const isBusinessLoan = loanTypeValue.includes("business");
        const menuItems = isBusinessLoan ? menuItemsBP : menuItemsCP;

        // Find menu item
        let menuItem = menuItems.find(item => item.displayName === page || item.label === page);

        // Check subsections if not found in top-level
        if (!menuItem) {
            for (const parent of menuItems) {
                if (parent.subsections) {
                    const sub = parent.subsections.find(subItem => subItem.displayName === page || subItem.label === page);
                    if (sub) {
                        menuItem = sub;
                        break;
                    }
                }
            }
        }

        // Override CorelogicCredco → DUAL AUS for Business Loan + NMLS ID
        if (isBusinessLoan && loanOfficerNMLSID && menuItem?.label === "CorelogicCredco") {
            menuItem = menuItemsCP.find(item => item.label === "DUAL AUS"); // fallback to CP for DUAL AUS config
        }

        // Perform validation check only if config is present
        if (menuItem?.validationExtPageId && menuItem?.validationPage) {
            const needsValidation = await checkValidationRequired(menuItem, loanId);
            if (needsValidation) {
                await tpoAppObject.pageRedirect("ExternalLoanPage", {
                    id: loanId,
                    extpageid: menuItem.validationExtPageId,
                    pagename: menuItem.validationPage
                });
                return;
            }
        }

        // Redirect to external page if extPageId is present
        if (menuItem?.extPageId) {
            await tpoAppObject.pageRedirect("ExternalLoanPage", {
                id: loanId,
                extpageid: menuItem.extPageId,
                pagename: menuItem.label
            });
        } else {
            // Default internal redirection
            await tpoAppObject.pageRedirect(menuItem.label, { id: loanId });
        }
    };

    // count only differences
    const diffCount = lcFields.filter(
        (f) =>
            (f.currentLoanValue ?? '').toString().trim() !==
            (f.currentLockValue ?? '').toString().trim()
    ).length;


    const msgLower = (licenseStatus.message || '').toLowerCase();

    const isRestrictionMessage =
        msgLower.includes('not eligible for')

    const sectionHeaderText = isRestrictionMessage ? 'Location Restriction' : 'Licensing';

    return (
        <>
            <LoadingOverlay loading={loading} text={loadingText} />

            <Toast
                visible={toastVisible}
                type="warning"
                message={toastMessage}
                onClose={() => {
                    setShowToast(false);
                    setToastVisible(false);
                }}
                animationClass={toastAnimation}
            />

            {/* ZIP City/State selection modal */}
            {showZipPopup && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
                    onClick={() => setShowZipPopup(false)}
                >
                    <div
                        className="bg-white rounded-lg p-6 w-96 shadow-lg"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">Select City & State</h3>
                            <button
                                className="text-gray-500 hover:text-black"
                                onClick={() => setShowZipPopup(false)}
                            >
                                ×
                            </button>
                        </div>
                        <ul className="space-y-2">
                            {cityOptions.map((opt, idx) => (
                                <li key={idx}>
                                    <button
                                        onClick={() => {
                                            document.getElementById(`field-${zipSelection.cityId}`).value =
                                                opt.city;
                                            document.getElementById(`field-${zipSelection.stateId}`).value =
                                                opt.abbrev;
                                            setShowZipPopup(false);
                                        }}
                                        className="w-full text-left px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded"
                                    >
                                        {opt.city}, {opt.abbrev}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            <div className="space-y-10 p-25px">
                <div className="rounded-lg p-3 space-y-6 max-w-8xl mx-auto">
                    <h2 className="page-title">{pageName}</h2>

                    {!loading && (
                        <>
                            {!skipModuleValidations && licenseStatus?.blocked ? (
                                /* --- Show ONLY Licensing when blocked --- */
                                <div className="border border-blue-200 rounded bg-gray-50">
                                    <div className="section-header">{sectionHeaderText}</div>
                                    <div className="p-4 space-y-4">
                                        <div className="flex items-start gap-3">
                                            <div className="mt-1 inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded bg-red-100 text-red-700">
                                                Blocking
                                            </div>
                                            <div>
                                                <div className="text-base font-semibold">
                                                    {isRestrictionMessage ? 'Location restriction' : 'Licensing verification required'}
                                                    {isRestrictionMessage && licenseStatus.city
                                                        ? ` for ${licenseStatus.city}, ${licenseStatus.stateCode}`
                                                        : licenseStatus.stateCode
                                                            ? ` for ${licenseStatus.stateCode} state`
                                                            : ''}
                                                </div>

                                                <p className="text-base text-red-700 mt-1 font-medium"
                                                    dangerouslySetInnerHTML={{ __html: licenseStatus.message }}>
                                                </p>
                                            </div>
                                        </div>
                                        {!isRestrictionMessage && (
                                            <div className="grid md:grid-cols-2 gap-4">
                                                <div className="border rounded p-3 bg-white">
                                                    <div className="text-sm font-medium mb-1">Company / Broker License</div>
                                                    <div className="text-sm">
                                                        {licenseStatus.companyRequired ? (
                                                            licenseStatus.companyHas ? (
                                                                <span className="inline-flex items-center text-green-700">
                                                                    <span className="w-2 h-2 rounded-full inline-block mr-2 bg-green-600" /> Found
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center text-red-700">
                                                                    <span className="w-2 h-2 rounded-full inline-block mr-2 bg-red-600" /> Missing
                                                                </span>
                                                            )
                                                        ) : (
                                                            <span className="inline-flex items-center text-gray-600">
                                                                <span className="w-2 h-2 rounded-full inline-block mr-2 bg-gray-400" /> Not Required
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="border rounded p-3 bg-white">
                                                    <div className="text-sm font-medium mb-1">Loan Originator License</div>
                                                    <div className="text-sm">
                                                        {licenseStatus.loRequired ? (
                                                            licenseStatus.loHas ? (
                                                                <span className="inline-flex items-center text-green-700">
                                                                    <span className="w-2 h-2 rounded-full inline-block mr-2 bg-green-600" /> Found
                                                                </span>
                                                            ) : (
                                                                <span className="inline-flex items-center text-red-700">
                                                                    <span className="w-2 h-2 rounded-full inline-block mr-2 bg-red-600" /> Missing
                                                                </span>
                                                            )
                                                        ) : (
                                                            <span className="inline-flex items-center text-gray-600">
                                                                <span className="w-2 h-2 rounded-full inline-block mr-2 bg-gray-400" /> Not Required
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                /* --- Normal module rendering when NOT blocked --- */
                                pageConfig?.FieldGroups
                                    .filter((group) => group.Type === 'Module')
                                    .filter((group) => shouldShowGroup(group))
                                    .filter((group) => !isModuleGroupCleared(group))
                                    .map((group, index) => (
                                        <div key={index} className="border border-blue-200 rounded bg-gray-50 mt-5">
                                            <div className="section-header">{group.Group}</div>
                                            {(() => {
                                                if (group.Group === 'Upload Documents' && isConditionBetaEnabled && uploadConditionsNeedsValidation) {
                                                    return (
                                                        <div className="px-4 pb-2 pt-2">
                                                            {group.Notes && (
                                                                <div
                                                                    className="text-sm text-red-700 font-semibold italic whitespace-pre-line"
                                                                    dangerouslySetInnerHTML={{ __html: group.Notes }}
                                                                />
                                                            )}
                                                            {uploadConditionsValidationItems.length > 0 && (
                                                                <ul className="mt-2 list-disc pl-5 text-sm text-red-700">
                                                                    {uploadConditionsValidationItems.map((item, idx) => (
                                                                        <li key={`${item.name}-${item.externalId}-${idx}`}>
                                                                            {item.name}{item.externalId ? ` (External ID: ${item.externalId})` : ''}
                                                                        </li>
                                                                    ))}
                                                                </ul>
                                                            )}
                                                        </div>
                                                    );
                                                }

                                                // Special handling for Rate Lock — Processing: map status -> friendly message
                                                if (group.Group === 'Rate Lock — Processing') {
                                                    const finalNote = getLockStatusNote(fieldValues['LOCKRATE.CURRENTSTATUS']);

                                                    return (
                                                        <div
                                                            className="text-sm text-red-700 font-semibold italic px-4 pb-1 pt-2 whitespace-pre-line"
                                                        >
                                                            {finalNote}
                                                        </div>
                                                    );
                                                }

                                                // Loan program blocked for this org: name the loan's product in the note
                                                if (group.Group === 'Loan Program Not Available') {
                                                    const productName = getBlockedLoanProgramName(fieldValues[group.ShowIf?.field], fieldValues['1401']);

                                                    return (
                                                        <div
                                                            className="text-sm text-red-700 font-semibold italic px-4 pb-1 pt-2 whitespace-pre-line"
                                                            dangerouslySetInnerHTML={{ __html: (group.Notes || '').split('{{LOAN_PROGRAM}}').join(productName) }}
                                                        />
                                                    );
                                                }

                                                // Default behavior: show whatever is in group.Notes (may contain markup)
                                                if (group.Notes) {
                                                    return (
                                                        <div
                                                            className="text-sm text-red-700 font-semibold italic px-4 pb-1 pt-2 whitespace-pre-line"
                                                            dangerouslySetInnerHTML={{ __html: group.Notes }}
                                                        />
                                                    );
                                                }

                                                return null;
                                            })()}

                                            {/* Special: Lock Comparison body */}
                                            {group.Group === 'Lock Comparison' ? (
                                                <div className="p-4 space-y-3">
                                                    <div className="flex items-center justify-between pb-2 flex-wrap gap-4">
                                                        <div className="flex flex-wrap items-center gap-4 text-sm">
                                                            <p className="text-gray-500">Field Changes: {diffCount}</p>
                                                            <p>Lock Date: {lcLockDate || '-'}</p>
                                                            <p>Expiration: {lcExpire || '-'}</p>
                                                            {lcStatus && (
                                                                <p className="text-red-600 font-semibold">{lcStatus}</p>
                                                            )}
                                                        </div>

                                                    </div>

                                                    <div className="overflow-x-auto">
                                                        <table className="w-full text-sm border-collapse mt-2">
                                                            <thead className="text-left bg-gray-100">
                                                                <tr>
                                                                    <th className="p-2 border w-1/4">Field Name</th>
                                                                    <th className="p-2 border w-1/4">Locked Value</th>
                                                                    <th className="p-2 border w-1/4">Current Loan Value</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {lcFields
                                                                    .filter(
                                                                        (f) =>
                                                                            (f.currentLoanValue ?? '').toString().trim() !==
                                                                            (f.currentLockValue ?? '').toString().trim()
                                                                    )
                                                                    .map((f) => (
                                                                        <tr key={f.lockFieldId || f.description}>
                                                                            <td className="p-2 border font-medium">
                                                                                {f.description}
                                                                            </td>
                                                                            <td className="p-2 border">
                                                                                {f.currentLockValue || '-'}
                                                                            </td>
                                                                            <td className="p-2 border bg-yellow-100 font-semibold text-red-700">
                                                                                {f.currentLoanValue || '-'}
                                                                            </td>
                                                                        </tr>
                                                                    ))}
                                                                {diffCount === 0 && (
                                                                    <tr>
                                                                        <td className="p-2 border italic text-gray-600" colSpan={3}>
                                                                            All fields match the current lock.
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                        <button
                                                            type="button"
                                                            className="mt-10 btn em-btn-primary"
                                                            id="field-4788"
                                                            onClick={() =>
                                                                redirectToPage('Product Pricing & Lock', loanId)
                                                            }
                                                        >
                                                            Re-Price
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                // Default module: render its buttons
                                                <div className="grid md:grid-cols-2 gap-6 p-4">
                                                    {group.Fields?.map((field) => (
                                                        <div key={field.id || field.name}>
                                                            {/* Hidden mirror input kept for consistency (no save) */}
                                                            {field.id && (
                                                                <input
                                                                    type="text"
                                                                    className="hidden"
                                                                    id={`field-${field.id}`}
                                                                    defaultValue={fieldValues[field.id]}
                                                                    readOnly
                                                                />
                                                            )}
                                                            <button
                                                                type="button"
                                                                className="mt-2 btn em-btn-tertiary"
                                                                onClick={() => redirectToPage(field.pagetoRedirect, loanId)}
                                                            >
                                                                {field.name}
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )
                                            }
                                        </div>
                                    ))
                            )}

                            {/* Only when all modules that should show are cleared, render FIELD groups */}
                            {allModulesCleared && (
                                <>
                                    <div className="flex justify-between items-center mt-4 mb-2">
                                        <p className="text-sm text-red-600 italic mb-2">
                                            All fields are required to {pageName}.
                                        </p>
                                        <button onClick={handleSave} className="btn em-btn-primary">
                                            Save
                                        </button>
                                    </div>

                                    {pageConfig?.FieldGroups
                                        .filter((group) => group.Type === 'Field')
                                        .filter((group) => shouldShowGroup(group))
                                        .map((group, index) => (
                                            <div key={index} className="border border-blue-200 rounded bg-gray-50 mt-5">
                                                <div className="section-header">{group.Group}</div>
                                                {group.Notes && (
                                                    <div
                                                        className="text-sm text-red-700 font-semibold italic px-4 pb-1 pt-2 whitespace-pre-line"
                                                        dangerouslySetInnerHTML={{ __html: group.Notes }}
                                                    />
                                                )}
                                                <div className="grid md:grid-cols-2 gap-6 p-4">
                                                    {group.Fields
                                                        .filter((field) => !field.hidden)
                                                        .filter((field) => {
                                                            // If group is forced visible via ShowIf, show all its fields
                                                            if (group.ShowIf) return true;

                                                            // If the field is conditionally required via JSON (requiredIf),
                                                            // hide the field when the requiredIf condition is not met.
                                                            if (field.requiredIf && field.requiredIf.field) {
                                                                const triggerFieldId = field.requiredIf.field;
                                                                const triggerOp = field.requiredIf.operator;
                                                                const triggerVal = fieldValues[triggerFieldId];

                                                                // Only support 'notEmpty' / 'empty' triggers here (matches your JSON pattern)
                                                                if (triggerOp === 'notEmpty' && isEmptyField(triggerVal)) {
                                                                    // condition not met -> field is not required and should not be shown
                                                                    return false;
                                                                }
                                                                if (triggerOp === 'empty' && !isEmptyField(triggerVal)) {
                                                                    // condition not met -> field should not be shown
                                                                    return false;
                                                                }
                                                            }
                                                            const value = fieldValues[field.id];
                                                            return isEmptyField(value) || isInvalidByValidate(field);
                                                        })
                                                        .map((field) => (
                                                            <div key={field.id}>
                                                                {field.datatype !== 'button' && (
                                                                    <label
                                                                        className="form-label mb-1"
                                                                        htmlFor={`field-${field.id}`}
                                                                    >
                                                                        {field.name} <span className="text-red-600">*</span>
                                                                    </label>
                                                                )}

                                                                {field.datatype === 'State' ? (
                                                                    <select
                                                                        className="form-input w-full text-sm border rounded p-1"
                                                                        id={`field-${field.id}`}
                                                                        defaultValue=""
                                                                    >
                                                                        <option value="">Select State</option>
                                                                        {US_STATES.map((state) => (
                                                                            <option key={state.abbrev} value={state.abbrev}>
                                                                                {state.name}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                ) : field.datatype === 'Date' ? (
                                                                    <input
                                                                        type="date"
                                                                        className="form-input-medium"
                                                                        id={`field-${field.id}`}
                                                                        defaultValue={
                                                                            fieldValues[field.id]
                                                                                ? new Date(fieldValues[field.id])
                                                                                    .toISOString()
                                                                                    .split('T')[0]
                                                                                : ''
                                                                        }
                                                                    />
                                                                ) : field.datatype === 'dropdown' ? (
                                                                    <select
                                                                        className="form-input w-full text-sm border rounded p-1"
                                                                        id={`field-${field.id}`}
                                                                        defaultValue={fieldValues[field.id] || ''}
                                                                    >
                                                                        <option value="">Select</option>
                                                                        {(field.options || []).map((opt) => (
                                                                            <option key={opt.value} value={opt.value}>
                                                                                {opt.text}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                ) : field.datatype === 'button' ? (
                                                                    <div>
                                                                        <input
                                                                            type="text"
                                                                            className="hidden"
                                                                            id={`field-${field.id}`}
                                                                            defaultValue={fieldValues[field.id]}
                                                                            onBlur={() =>
                                                                                field.datatype === 'Zip' && handleZipBlur(field)
                                                                            }
                                                                        />
                                                                        <button
                                                                            type="button"
                                                                            className="mt-2 btn em-btn-tertiary"
                                                                            onClick={() =>
                                                                                redirectToPage(field.pagetoRedirect, loanId)
                                                                            }
                                                                        >
                                                                            {field.name}
                                                                        </button>
                                                                    </div>
                                                                ) : field.datatype === 'decimal' ? (
                                                                    <input
                                                                        type="text"
                                                                        inputMode="decimal"
                                                                        pattern="^\d+(\.\d+)?$"
                                                                        className="form-input"
                                                                        id={`field-${field.id}`}
                                                                        defaultValue={fieldValues[field.id]}
                                                                        onInput={(e) => {
                                                                            e.target.value = filterDecimalKeystrokes(e.target.value);
                                                                        }}
                                                                        onBlur={(e) => {
                                                                            e.target.value = normalizeDecimal(e.target.value, {
                                                                                min: 0,          // clamp lower bound
                                                                                max: 100,        // clamp upper bound
                                                                                decimals: 3      // allow up to 3 decimals
                                                                            });
                                                                        }}
                                                                        title="Enter a decimal value"
                                                                    />
                                                                ) : (
                                                                    <input
                                                                        type="text"
                                                                        className="form-input"
                                                                        id={`field-${field.id}`}
                                                                        defaultValue={fieldValues[field.id]}
                                                                        onBlur={() =>
                                                                            field.datatype === 'Zip' && handleZipBlur(field)
                                                                        }
                                                                    />
                                                                )}
                                                            </div>
                                                        ))}
                                                </div>
                                            </div>
                                        ))}

                                    <div className="text-right mt-8">
                                        <button onClick={handleSave} className="btn em-btn-primary">
                                            Save
                                        </button>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default SubmitLoanPageRenderer;